import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';
import type { AutocompleteProvider } from '../../types';
import { BlockEditPopover } from '../../components/BlockEditPopover';
import { parseResolvedTokens, tokenStringToPlainText } from '../../utils/resolvedTokens';

export const HandlebarsTagView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  selected,
  updateAttributes,
  extension,
}) => {
  const { variable, description, isValid, error, nonEditable } = node.attrs;

  const [isEditing, setIsEditing] = useState(false);
  const [editAnchorRect, setEditAnchorRect] = useState<DOMRect | null>(null);

  const autocompleteProvider = extension.options.autocompleteProvider as AutocompleteProvider | undefined;

  const tagClasses = [
    'handlebars-tag',
    selected ? 'selected' : '',
    !isValid ? 'invalid' : '',
    description ? 'has-description' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const descriptionTokens = useMemo(() => parseResolvedTokens(description), [description]);
  const hasDescription = !!description;

  // Show description if available, otherwise show the variable name
  const displayText = (description && !descriptionTokens)
    ? description
    : tokenStringToPlainText(description) || variable;

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const button = e.currentTarget as HTMLElement;
      setEditAnchorRect(button.getBoundingClientRect());
      setIsEditing(true);
    },
    []
  );

  return (
    <NodeViewWrapper as="span" className="handlebars-tag-wrapper">
      <span className={tagClasses} data-drag-handle="" title={`{{${variable}}}`}>
        {!hasDescription && <span className="braces">{'{{'}</span>}
        {descriptionTokens ? (
          <span className="variable">
            {descriptionTokens.map((token, i) =>
              token.isResolved
                ? <span key={i} className="resolved-token">{token.text}</span>
                : <span key={i}>{i > 0 ? ' ' : ''}{token.text}{i < descriptionTokens.length - 1 ? ' ' : ''}</span>
            )}
          </span>
        ) : (
          <span className="variable">{displayText}</span>
        )}
        {!hasDescription && <span className="braces">{'}}'}</span>}
        {error && (
          <span className="error-indicator" title={error}>
            !
          </span>
        )}
        {!nonEditable && autocompleteProvider && (
          <button
            type="button"
            className="tag-edit-button"
            onClick={handleEdit}
            title="Edit variable"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
      </span>

      {isEditing && editAnchorRect && autocompleteProvider && (
        <BlockEditPopover
          initialValue={variable}
          anchorRect={editAnchorRect}
          provider={autocompleteProvider}
          editor={editor}
          blockPos={typeof getPos() === 'number' ? (getPos() as number) : 0}
          showDescriptions={extension.options.showDescriptions as boolean}
          allowFreeformHandlebars={extension.options.allowFreeformHandlebars as boolean}
          onConfirm={(newVariable) => {
            updateAttributes({ variable: newVariable, description: null });
            setIsEditing(false);
            editor.commands.focus();
          }}
          onClose={() => {
            setIsEditing(false);
            editor.commands.focus();
          }}
        />
      )}
    </NodeViewWrapper>
  );
};

export default HandlebarsTagView;
